import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, CheckCircle, AlertTriangle, XCircle, ArrowDown, ArrowUp, MinusCircle } from "lucide-react";

const STATUS_OPTIONS = ["normal", "elevated", "critical"];
const STATUS_COLORS = {
  normal: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" },
  elevated: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500" },
  critical: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", dot: "bg-red-500" },
  low: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" },
  not_assessed: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-500", dot: "bg-slate-400" },
};
const STATUS_LABELS = { not_assessed: "n/a" };

const getGrade = (rating) => {
  if (rating >= 85) return { grade: "A", label: "Excellent" };
  if (rating >= 70) return { grade: "B", label: "Good" };
  if (rating >= 55) return { grade: "C", label: "Fair" };
  if (rating >= 40) return { grade: "D", label: "Poor" };
  return { grade: "F", label: "Critical" };
};

const StatusIcon = ({ status }) => {
  if (status === "critical") return <XCircle size={14} className="text-red-500" />;
  if (status === "elevated") return <AlertTriangle size={14} className="text-amber-500" />;
  if (status === "not_assessed") return <MinusCircle size={14} className="text-slate-400" />;
  return <CheckCircle size={14} className="text-emerald-500" />;
};

export default function MFCWLOverrideModal({ open, onOpenChange, analysis, onSuccess }) {
  const [sounds, setSounds] = useState([]);
  const [engineRating, setEngineRating] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (analysis && open) {
      const st = analysis.sound_classification?.sound_types || [];
      setSounds(st.map(s => ({ ...s })));
      setEngineRating(analysis.engine_rating?.overall_rating || 0);
      setDirty(false);
    }
  }, [analysis, open]);

  const criticalCount = sounds.filter(s => s.status === "critical").length;
  const elevatedCount = sounds.filter(s => s.status === "elevated").length;
  const normalCount = sounds.filter(s => !["critical", "elevated", "not_assessed"].includes(s.status)).length;
  const { grade, label } = getGrade(engineRating);
  const ratingOutOf10 = Math.round((engineRating / 10) * 10) / 10;
  const valuationImpact = Math.round(-(1 - ratingOutOf10 / 10) * 15 * 10) / 10;

  const updateSound = (idx, field, value) => {
    setSounds(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
    setDirty(true);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        code: "539953",
        rating: engineRating,
        sound_overrides: sounds.map(s => ({
          name: s.name,
          status: s.status,
          measured_db: parseFloat(s.measured_db) || 0,
          ideal_db: parseFloat(s.ideal_db) || 0,
        })),
        predictive_data: {
          valuation_impact_pct: Math.abs(valuationImpact),
        },
      };
      const resp = await api.directOverride(analysis.id, payload);
      toast.success(`Updated: ${resp.data.updates?.join(", ") || "applied"}`);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || err.message || "Override failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const car = analysis?.car_details || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mfcwl-override-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold uppercase tracking-wider">
            MFCWL Override
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            {car.make} {car.model} ({car.year}) — Edit sound parameters and engine rating
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Engine Rating */}
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="engine-rating-section">
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Engine Rating</p>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={engineRating}
                  onChange={(e) => { setEngineRating(parseInt(e.target.value) || 0); setDirty(true); }}
                  className="w-24 text-center font-mono text-lg font-bold"
                  data-testid="engine-rating-input"
                />
                <span className="text-base text-slate-500">/100</span>
                <span className="text-lg font-bold text-slate-400">=</span>
                <span className="text-xl font-bold text-slate-800">{ratingOutOf10}/10</span>
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${
                  grade === "A" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                  grade === "B" ? "bg-sky-100 text-sky-800 border-sky-200" :
                  grade === "C" ? "bg-amber-100 text-amber-800 border-amber-200" :
                  grade === "D" ? "bg-orange-100 text-orange-800 border-orange-200" :
                  "bg-red-100 text-red-800 border-red-200"
                }`}>
                  {grade} — {label}
                </span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">Valuation Impact</p>
              <p className={`text-xl font-bold font-mono ${valuationImpact < 0 ? "text-red-600" : "text-emerald-600"}`}>
                {valuationImpact > 0 ? "+" : ""}{valuationImpact}%
              </p>
            </div>
          </div>

          {/* Sound Summary */}
          <div className="flex gap-3" data-testid="sound-summary">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 border border-red-200">
              <XCircle size={12} className="text-red-500" />
              <span className="text-xs font-bold text-red-700">{criticalCount} Critical</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200">
              <AlertTriangle size={12} className="text-amber-500" />
              <span className="text-xs font-bold text-amber-700">{elevatedCount} Elevated</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
              <CheckCircle size={12} className="text-emerald-500" />
              <span className="text-xs font-bold text-emerald-700">{normalCount} Normal</span>
            </div>
          </div>

          {/* Sound Parameters Table */}
          <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="sound-table">
            <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 grid grid-cols-12 gap-2 items-center">
              <div className="col-span-4 text-xs font-bold uppercase tracking-wider text-slate-500">Sound Type</div>
              <div className="col-span-2 text-xs font-bold uppercase tracking-wider text-slate-500 text-center">Measured dB</div>
              <div className="col-span-2 text-xs font-bold uppercase tracking-wider text-slate-500 text-center">Ideal dB</div>
              <div className="col-span-2 text-xs font-bold uppercase tracking-wider text-slate-500 text-center">Status</div>
              <div className="col-span-2 text-xs font-bold uppercase tracking-wider text-slate-500 text-center">Preview</div>
            </div>
            <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
              {sounds.map((s, i) => {
                const colors = STATUS_COLORS[s.status] || STATUS_COLORS.normal;
                return (
                  <div
                    key={s.name}
                    className={`grid grid-cols-12 gap-2 items-center px-4 py-2.5 transition-colors ${colors.bg}`}
                    data-testid={`sound-row-${i}`}
                  >
                    <div className="col-span-4 flex items-center gap-2">
                      <StatusIcon status={s.status} />
                      <span className="text-sm font-medium text-slate-700 truncate">{s.name}</span>
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        step="0.1"
                        value={s.measured_db ?? ""}
                        onChange={(e) => updateSound(i, "measured_db", parseFloat(e.target.value) || 0)}
                        className={`h-8 text-center text-sm font-mono ${colors.border}`}
                        data-testid={`measured-${i}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        step="0.1"
                        value={s.ideal_db ?? ""}
                        onChange={(e) => updateSound(i, "ideal_db", parseFloat(e.target.value) || 0)}
                        className={`h-8 text-center text-sm font-mono ${colors.border}`}
                        data-testid={`ideal-${i}`}
                      />
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <select
                        value={s.status}
                        onChange={(e) => updateSound(i, "status", e.target.value)}
                        className={`h-8 px-2 text-xs font-bold uppercase rounded border ${colors.border} ${colors.bg} ${colors.text} cursor-pointer`}
                        data-testid={`status-${i}`}
                      >
                        {(STATUS_OPTIONS.includes(s.status) || !s.status ? STATUS_OPTIONS : [s.status, ...STATUS_OPTIONS]).map(opt => (
                          <option key={opt} value={opt}>{STATUS_LABELS[opt] || opt}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <div className="w-full h-5 bg-slate-200 rounded-sm overflow-hidden relative">
                        <div
                          className="absolute inset-y-0 left-0 rounded-sm transition-all"
                          style={{
                            width: `${Math.min(100, ((s.measured_db || 0) / 100) * 100)}%`,
                            backgroundColor: s.status === "critical" ? "#EF4444" : s.status === "elevated" ? "#FFB347" : s.status === "not_assessed" ? "#94A3B8" : "#4CD964",
                          }}
                        />
                        <div
                          className="absolute inset-y-0 w-0.5 bg-slate-800 z-10"
                          style={{ left: `${Math.min(100, ((s.ideal_db || 0) / 100) * 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="bg-indigo-600 text-white hover:bg-indigo-700"
            disabled={loading || !dirty}
            data-testid="mfcwl-override-submit"
          >
            {loading ? (
              <><Loader2 size={14} className="mr-2 animate-spin" /> Applying...</>
            ) : (
              "Apply Changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
